import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { Banner } from '@src/common/components/Banner';
import { Card } from '@src/common/components/Card';
import { PageLayout } from '@src/common/components/Page';
import { Stack } from '@src/common/components/Stack';
import { Typography } from '@src/common/components/Typography';
import { MeasurementToggle } from '@src/common/measurements/MeasurementToggle';
import { css } from '@src/common/styles/Styles';
import { theme } from '@src/common/styles/Theme';
import { LastUpdatedBanner } from '@src/orders/components/LastUpdatedBanner';
import type { Order, OrderPermitApplication } from '@src/orders/domain/Order';
import { usePermitMeasurement } from '@src/permits/hooks/usePermitMeasurement';
import { Permit } from '@src/permits/Permit';
import type { ProcessWithJurisdictionDocument } from '@src/permits/SubmitPermitForm';
import { SubmitPermitForm } from '@src/permits/SubmitPermitForm';
import { Trans, useTranslation } from 'react-i18next';

interface Props {
  orderPermitApplication: OrderPermitApplication;
  order: Order;
  canSaveProgress?: boolean;
  onSubmit: (data: {
    confirmationNumber: string;
    documents?: ProcessWithJurisdictionDocument[];
  }) => void;
}

export const ProcessWithJurisdiction = ({
  orderPermitApplication,
  order,
  onSubmit,
  canSaveProgress = true,
}: Props) => {
  const { t } = useTranslation();
  const { showInMetric, setShowInMetric } = usePermitMeasurement();

  const { application: permitApplication } = orderPermitApplication;
  const showBanner =
    order.changeRequests?.getLatestChangeRequest() &&
    order.changeRequests!.getLatestChangeRequest()!.requestedAt >
      permitApplication.createdAt.getDate();

  return (
    <PageLayout
      title={t('processWithJurisdictionTitle')}
      subtitle={t('permit.header', { permitId: permitApplication.id })}
      style={{ gap: theme.spacing['8'] }}
    >
      {showBanner && <LastUpdatedBanner />}
      <Card className={container}>
        <Stack gap={theme.spacing['4']}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography variant="subtitle1">
              {t('permitInformation')}
            </Typography>

            <MeasurementToggle
              isMetric={showInMetric}
              setIsMetric={setShowInMetric}
            />
          </Stack>
          <Banner severity="info">
            <ContentCopyIcon />
            <Typography variant="body1" style={{ fontWeight: '500' }}>
              <Trans
                i18nKey="copyFieldInfo"
                components={{ strong: <strong style={{ fontWeight: 600 }} /> }}
              />
            </Typography>
          </Banner>
        </Stack>

        <Permit
          isMetric={showInMetric}
          order={order}
          permitApplication={orderPermitApplication.application}
        />
      </Card>

      <SubmitPermitForm
        orderPermitApplication={orderPermitApplication}
        orderId={order.id}
        className={container}
        onSubmit={onSubmit}
        canSaveProgress={canSaveProgress}
      />
    </PageLayout>
  );
};

const container = css((theme) => ({
  padding: `${theme.spacing['8']}`,
  gap: `${theme.spacing['8']}`,
  display: 'flex',
  flexDirection: 'column',
}));
